import React, { useContext, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { CartContext } from '../context/CartContext';

// Use the environment variable for your live Stripe public key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const CheckoutContent = () => {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const { cart, getTotalPrice, clearCart } = useContext(CartContext);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    province: '',
    postalCode: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [fulfillmentMethod, setFulfillmentMethod] = useState('delivery'); // ✅ Step 1: State added
  const orderIdRef = useRef(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const subtotal = getTotalPrice();
  // ✅ Step 2: Shipping becomes $0 for pickup client-side summary
  const shipping =
    fulfillmentMethod === 'pickup'
      ? 0
      : subtotal > 100
      ? 0
      : 15;
  const tax = subtotal * 0.13;
  const total = subtotal + tax + shipping;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!stripe || !elements) {
      setError('Payment system not loaded. Please refresh the page.');
      return;
    }

    setLoading(true);

    try {
      // Step 1: Create order and payment intent on the server
      console.log('Creating order and payment intent...');
      const paymentResponse = await fetch(
        'https://us-central1-northpet.cloudfunctions.net/createOrderPaymentIntent',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: cart.map((item) => ({
              productId: item.id,
              quantity: item.quantity,
            })),
            currency: 'cad',
            email: formData.email,
            description: `NorthPet Inc. Purchase`,
            customer: {
              name: `${formData.firstName} ${formData.lastName}`,
              phone: formData.phone,
            },
            // ✅ Step 3: Send fulfillmentMethod and shippingCost to Firebase
            fulfillmentMethod,
            shippingCost: shipping,
            // Always send a shippingAddress object. For pickup, send the store pickup address
            // so the server has a consistent shape and required fields are present.
            shippingAddress:
              fulfillmentMethod === 'pickup'
                ? {
                    address: 'NorthPet Inc. 35 Selfridge Way',
                    city: 'Whitby',
                    province: 'ON',
                    postalCode: 'L1N 0N9',
                    pickup: true,
                  }
                : {
                    address: formData.address,
                    city: formData.city,
                    province: formData.province,
                    postalCode: formData.postalCode,
                  },
          }),
        }
      );

      if (!paymentResponse.ok) {
        const errorData = await paymentResponse.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to create order and payment intent');
      }

      const paymentData = await paymentResponse.json();
      const { clientSecret, paymentIntentId, orderId } = paymentData;

      orderIdRef.current = orderId;

      console.log('Order and payment intent created:', orderId, paymentIntentId);

      // Step 2: Confirm payment with Stripe
      console.log('Processing payment with Stripe...');
      const cardElement = elements.getElement(CardElement);
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: `${formData.firstName} ${formData.lastName}`,
            email: formData.email,
            // ✅ Step 9: Updated Billing Details Omission (Spread Method)
            ...(fulfillmentMethod === 'delivery' && {
              address: {
                line1: formData.address,
                city: formData.city,
                state: formData.province,
                postal_code: formData.postalCode,
              },
            }),
          },
        },
      });

      if (result.error) {
        setError(result.error.message);
        console.error('Payment failed:', result.error);
      } else if (result.paymentIntent.status === 'succeeded') {
        console.log('Payment succeeded:', result.paymentIntent.id);
        setSuccess(true);
        clearCart();
        
        setTimeout(() => {
          alert(`✅ Order successful!\n\nOrder ID: ${orderId}\n\nThank you for your purchase!`);
          navigate('/products');
        }, 1500);
      }
    } catch (err) {
      setError(err.message || 'An error occurred during checkout');
      console.error('Checkout error:', err);
    } finally {
      setLoading(false);
    }
  };

  const CARD_ELEMENT_OPTIONS = {
    style: {
      base: {
        color: '#32325d',
        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
        fontSmoothing: 'antialiased',
        fontSize: '16px',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#fa755a',
        iconColor: '#fa755a',
      },
    },
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 pb-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Your cart is empty</h2>
          <button
            onClick={() => navigate('/products')}
            className="bg-forest-green text-white px-8 py-3 rounded-lg font-semibold"
          >
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-20">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl font-bold text-forest-green mb-12">Checkout</h1>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Checkout Form Inputs */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-lg p-8 space-y-6">
              {/* Contact Information */}
              <div>
                <h2 className="text-xl font-bold text-gray-800 mb-4">
                  Contact Information
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="text"
                    name="firstName"
                    placeholder="First Name *"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-forest-green"
                    required
                  />
                  <input
                    type="text"
                    name="lastName"
                    placeholder="Last Name *"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-forest-green"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <input
                    type="email"
                    name="email"
                    placeholder="Email *"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-forest-green"
                    required
                  />
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-forest-green"
                  />
                </div>
              </div>

              {/* ✅ Step 4: Fulfillment Method section added */}
              {/* Fulfillment Method */}
              <div>
                <h2 className="text-xl font-bold text-gray-800 mb-4">
                  Fulfillment Method
                </h2>

                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="fulfillmentMethod"
                      value="delivery"
                      checked={fulfillmentMethod === 'delivery'}
                      onChange={(e) => setFulfillmentMethod(e.target.value)}
                    />
                    <span>Delivery</span>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="fulfillmentMethod"
                      value="pickup"
                      checked={fulfillmentMethod === 'pickup'}
                      onChange={(e) => setFulfillmentMethod(e.target.value)}
                    />
                    <span>Local Pickup (Free)</span>
                  </label>
                </div>

                {fulfillmentMethod === 'pickup' && (
                  <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <h3 className="font-semibold text-green-800 mb-2">
                      Pickup Location
                    </h3>

                    <p>NorthPet Inc.</p>
                    <p>35 Selfridge Way</p>
                    <p>Whitby, ON L1N 0N9</p>

                    {/* ✅ Additional Pickup Information Displayed */}
                    <h3 className="font-semibold text-green-800 mt-4 mb-2">
                      Pickup Date
                    </h3>
                    <p>We will contact you via email when your order is ready for pickup.</p>

                    <p className="mt-3 text-sm text-green-700">
                      Shipping charges will not apply to pickup orders.
                    </p>
                  </div>
                )}
              </div>

              {/* ✅ Step 5: Shipping fields hidden when Pickup is selected */}
              {/* Shipping Address */}
              {fulfillmentMethod === 'delivery' && (
                <div>
                  <h2 className="text-xl font-bold text-gray-800 mb-4">
                    Shipping Address
                  </h2>
                  <input
                    type="text"
                    name="address"
                    placeholder="Address *"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-forest-green mb-4"
                    // ✅ Step 6: Shipping fields required only for delivery
                    required={fulfillmentMethod === 'delivery'}
                  />
                  <div className="grid grid-cols-3 gap-4">
                    <input
                      type="text"
                      name="city"
                      placeholder="City *"
                      value={formData.city}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-forest-green"
                      required={fulfillmentMethod === 'delivery'}
                    />
                    <input
                      type="text"
                      name="province"
                      placeholder="Province *"
                      value={formData.province}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-forest-green"
                      required={fulfillmentMethod === 'delivery'}
                    />
                    <input
                      type="text"
                      name="postalCode"
                      placeholder="Postal Code *"
                      value={formData.postalCode}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-forest-green"
                      required={fulfillmentMethod === 'delivery'}
                    />
                  </div>
                </div>
              )}

              {/* Payment Info */}
              <div>
                <h2 className="text-xl font-bold text-gray-800 mb-4">
                  Payment Information
                </h2>
                {/* ✅ Step 7: Test Card notice removed */}
                
                <div className="p-4 border border-gray-300 rounded-lg bg-white mb-6">
                  <CardElement options={CARD_ELEMENT_OPTIONS} />
                </div>
              </div>

              {error && (
                <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                  ❌ {error}
                </div>
              )}

              {success && (
                <div className="p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                  ✅ Payment successful! Redirecting...
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !stripe || success}
                className="w-full bg-forest-green text-white py-3 rounded-lg font-semibold hover:bg-opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? '🔄 Processing Payment...' : success ? '✅ Payment Complete' : 'Complete Purchase'}
              </button>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-24">
              <h2 className="text-2xl font-bold text-forest-green mb-6">
                Order Summary
              </h2>

              <div className="mb-6 space-y-3 border-b pb-6 max-h-96 overflow-y-auto">
                {cart.map((item) => (
                  <div key={item.id} className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      {item.name} x {item.quantity}
                    </span>
                    <span className="font-semibold">
                      ${(item.price * item.quantity).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>

              <div className="space-y-2 text-sm mb-6">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal:</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  {/* ✅ Step 8: Order summary labels and values updated */}
                  <span>
                    {fulfillmentMethod === 'pickup'
                      ? 'Pickup'
                      : 'Shipping'}
                  </span>
                  <span>
                    {fulfillmentMethod === 'pickup'
                      ? 'FREE PICKUP'
                      : shipping === 0
                      ? 'FREE'
                      : `$${shipping.toFixed(2)}`}
                  </span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Tax (13% HST):</span>
                  <span>${tax.toFixed(2)}</span>
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="flex justify-between text-xl font-bold text-forest-green">
                  <span>Total:</span>
                  <span>${total.toFixed(2)} CAD</span>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

function CheckoutPage() {
  return (
    <Elements stripe={stripePromise}>
      <CheckoutContent />
    </Elements>
  );
}

export default CheckoutPage;